import { useState, useEffect } from "react";
import axios from "axios";
import {
  X, Loader2, Plus, Zap, Play, Trash2, GripVertical,
  Bot, Settings, ChevronRight, CheckCircle2, ArrowRight,
} from "lucide-react";
import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const AGENT_LIBRARY = [
  { name: "Assessment Generator", category: "Content", description: "Generate MCQs, short-answer, and scenario-based assessments", icon: "quiz" },
  { name: "Metadata Tagger", category: "Content", description: "Auto-tag content with taxonomy, subject, and difficulty levels", icon: "tag" },
  { name: "Standards Aligner", category: "Compliance", description: "Map content to Common Core, NGSS, and other standards", icon: "align" },
  { name: "A11y Remediator", category: "Accessibility", description: "Fix accessibility issues: alt text, captions, reading order", icon: "a11y" },
  { name: "Glossary Generator", category: "Content", description: "Extract and define key terms with cross-references", icon: "book" },
  { name: "TTS Audio", category: "Media", description: "Generate word-synced narration audio from text content", icon: "audio" },
  { name: "Content Summarizer", category: "Content", description: "Create chapter summaries and key takeaways", icon: "summary" },
  { name: "Mastery Path Builder", category: "Learning", description: "Build structured learning paths with modules", icon: "path" },
  { name: "Flashcard Generator", category: "Content", description: "Generate study flashcards per chapter", icon: "cards" },
  { name: "A11y Analyser", category: "Accessibility", description: "Run WCAG 2.1 compliance audit with severity scores", icon: "scan" },
  { name: "Skill Gap Analyser", category: "Analytics", description: "Identify learning objective coverage gaps", icon: "gap" },
  { name: "Pacing Guide", category: "Learning", description: "Create curriculum pacing plans", icon: "pace" },
  { name: "Answer Key Generator", category: "Content", description: "Generate instructor answer keys for assessments", icon: "key" },
];

const CATEGORIES = [...new Set(AGENT_LIBRARY?.map(a => a.category))];

const CATEGORY_COLORS = {
  Content: "bg-blue-50 text-blue-700 border-blue-200",
  Compliance: "bg-purple-50 text-purple-700 border-purple-200",
  Accessibility: "bg-amber-50 text-amber-700 border-amber-200",
  Media: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Learning: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Analytics: "bg-rose-50 text-rose-700 border-rose-200",
};

export const AgentBuilder = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [filterCat, setFilterCat] = useState("All");
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const filtered = filterCat === "All" ? AGENT_LIBRARY : AGENT_LIBRARY?.filter(a => a.category === filterCat);

  const toggleAgent = (agent) => {
    setSelectedAgents(prev =>
      prev.find(a => a.name === agent.name)
        ? prev?.filter(a => a.name !== agent.name)
        : [...prev, agent]
    );
  };

  const removeAgent = (agentName) => {
    setSelectedAgents(prev => prev?.filter(a => a.name !== agentName));
  };

  const handleSave = async () => {
    if (!name.trim() || selectedAgents.length === 0 || saving) return;
    setSaving(true);
    try {
      const colors = ["from-[#1a6fc4] to-[#2d9cdb]", "from-[#6941c6] to-[#9b72ef]", "from-[#1e8a4a] to-[#22c55e]", "from-[#c8841a] to-[#f59e0b]", "from-[#0e7490] to-[#06b6d4]"];
      const { data } = await axios.post(`${API}/orchestrations`, {
        name: name.trim(),
        description: description.trim(),
        agents: selectedAgents?.map(a => a.name),
        type: "pipeline",
        color: colors[Math.floor(Math.random() * colors.length)],
      }, { withCredentials: true });
      onCreated?.(data);
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="agent-builder-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" style={{ animation: "cardFadeIn 0.2s ease-out" }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-border/40 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#014d7d] to-[#178a55] flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-base text-foreground">Agent Builder</h2>
              <p className="text-[11px] text-foreground/40">Create an AI pipeline from available agents</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Step indicators */}
            <div className="flex items-center gap-1 mr-3">
              {[1, 2, 3]?.map(s => (
                <div key={s} className={`w-6 h-1 rounded-full ${step >= s ? "bg-[#014d7d]" : "bg-gray-200"}`} />
              ))}
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-foreground/5 flex items-center justify-center" data-testid="close-agent-builder">
              <X className="w-4 h-4 text-foreground/40" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Name & Description */}
          {step === 1 && (
            <div className="p-6 space-y-4" data-testid="builder-step-1">
              <div>
                <label className="text-xs font-bold text-foreground/40 uppercase tracking-wider mb-1.5 block">Pipeline Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Full Assessment Suite"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-border/50 outline-none focus:border-[#014d7d]/40 bg-[#f8f9fb] placeholder:text-foreground/45"
                  data-testid="builder-name-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-foreground/40 uppercase tracking-wider mb-1.5 block">Description</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this pipeline do?"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-border/50 outline-none focus:border-[#014d7d]/40 bg-[#f8f9fb] placeholder:text-foreground/45 resize-none" rows={3}
                  data-testid="builder-desc-input" />
              </div>
            </div>
          )}

          {/* Step 2: Select Agents */}
          {step === 2 && (
            <div className="p-6" data-testid="builder-step-2">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button onClick={() => setFilterCat("All")} className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border ${filterCat === "All" ? "bg-[#014d7d] text-white border-[#014d7d]" : "border-border/40 text-foreground/40 hover:border-foreground/20"}`}>All</button>
                {CATEGORIES?.map(c => (
                  <button key={c} onClick={() => setFilterCat(c)} className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border ${filterCat === c ? "bg-[#014d7d] text-white border-[#014d7d]" : "border-border/40 text-foreground/40 hover:border-foreground/20"}`}>{c}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {filtered?.map(agent => {
                  const isSelected = selectedAgents.find(a => a.name === agent.name);
                  const catColor = CATEGORY_COLORS[agent.category] || "bg-gray-50 text-gray-700 border-gray-200";
                  return (
                    <button key={agent.name} onClick={() => toggleAgent(agent)}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${isSelected ? "border-[#014d7d] bg-[#014d7d]/[0.03]" : "border-border/40 hover:border-foreground/20"}`}
                      data-testid={`agent-option-${agent.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-foreground">{agent.name}</span>
                        {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-[#014d7d]" />}
                      </div>
                      <p className="text-[10px] text-foreground/40 mb-1.5">{agent.description}</p>
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md border ${catColor}`}>{agent.category}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Review Pipeline */}
          {step === 3 && (
            <div className="p-6 space-y-4" data-testid="builder-step-3">
              <div className="bg-[#f8f9fb] rounded-xl p-4 border border-border/40">
                <h3 className="text-sm font-bold text-foreground mb-1">{name || "Untitled Pipeline"}</h3>
                <p className="text-xs text-foreground/40">{description || "No description"}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Pipeline Sequence ({selectedAgents.length} agents)</p>
                <div className="space-y-2">
                  {selectedAgents?.map((agent, i) => (
                    <div key={agent.name} className="flex items-center gap-3" data-testid={`pipeline-agent-${i}`}>
                      {i > 0 && <div className="w-px h-4 bg-border/40 ml-4 -mt-4 -mb-2" />}
                      <div className="flex items-center gap-3 flex-1 bg-white border border-border/50 rounded-xl px-4 py-3">
                        <div className="w-6 h-6 rounded-lg bg-[#014d7d]/8 flex items-center justify-center text-[10px] font-bold text-[#014d7d]">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground">{agent.name}</p>
                          <p className="text-[10px] text-foreground/50">{agent.description}</p>
                        </div>
                        <button onClick={() => removeAgent(agent.name)} className="w-6 h-6 rounded-lg hover:bg-red-50 flex items-center justify-center text-foreground/45 hover:text-red-500" data-testid={`remove-agent-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {i < selectedAgents.length - 1 && <ArrowRight className="w-3 h-3 text-foreground/15 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/40 flex items-center justify-between shrink-0">
          <div className="text-[10px] text-foreground/50">
            {selectedAgents.length > 0 && <span>{selectedAgents.length} agent{selectedAgents.length > 1 ? "s" : ""} selected</span>}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} className="text-xs font-semibold px-4 py-2 rounded-lg text-foreground/50 hover:bg-foreground/5" data-testid="builder-back">Back</button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(s => s + 1)}
                disabled={(step === 1 && !name.trim()) || (step === 2 && selectedAgents.length === 0)}
                className="text-xs font-semibold px-5 py-2 rounded-lg bg-[#014d7d] text-white disabled:opacity-40 flex items-center gap-1.5"
                data-testid="builder-next">
                Next <ChevronRight className="w-3 h-3" />
              </button>
            ) : (
              <button onClick={handleSave} disabled={saving || !name.trim() || selectedAgents.length === 0}
                className="text-xs font-semibold px-5 py-2 rounded-lg bg-[#014d7d] text-white disabled:opacity-40 flex items-center gap-1.5"
                data-testid="builder-save">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Create Pipeline
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
